import { Component, Vue } from 'vue-property-decorator';
import { AxiosError } from 'axios';
import { Rpc } from '@/models/Rpc';
import ErrorHandler from '@/support/ErrorHandler';

/**
 * Maia's instructions — what she is told at the start of every conversation.
 *
 * Two texts, edited and published separately: the styling doctrine, and the
 * rules that are added after it. Maison365 asked to be able to change both
 * themselves rather than raising a ticket for every wording change. Publishing
 * takes effect on the very next message; nothing is cached and nothing needs a
 * deploy, and every version is kept, so an earlier wording can be read back out
 * of maia_prompt_revisions if a change turns out badly.
 *
 * What no wording in either box can reach: stock, excluded colours, "Geen Jurk"
 * and her size. Those are SQL in MaiaCatalogueSearch rather than prompt text,
 * which is what makes the fields safe to hand over — most of what reads like a
 * rule in these texts is a description of something the query already
 * guarantees.
 */
@Component<MaiaInstructions>({})
export default class MaiaInstructions extends Vue {
  // #region Class properties

  protected doctrine: InstructionSection = new InstructionSection({
    field: 'body',
    label: 'Styling doctrine',
    hint: 'Opens every conversation, before Maia sees the customer or searches the catalogue.',
    rows: 20,
    minLength: 200,
    maxLength: 60000,
  });

  protected rules: InstructionSection = new InstructionSection({
    field: 'rules',
    label: 'What Maia must never do',
    hint: 'Added after the doctrine. Stock, excluded colours, "Geen Jurk" and her size stay enforced in the query whatever this says.',
    rows: 10,
    minLength: 50,
    maxLength: 20000,
  });

  protected isLoading: boolean = true;

  // #endregion

  // #region Lifecycle Hooks / Init

  protected mounted(): void {
    this.fetchInstructions();
  }

  // #endregion

  // #region Handlers

  protected onSaveButtonClick(section: InstructionSection): void {
    if (!section.canSave || this.isLoading) {
      return;
    }

    this.publish(section);
  }

  /**
   * Put the delivered text back in the editor.
   *
   * Deliberately does not publish on its own — it fills the box and leaves the
   * administrator to read it and press Publish. Restoring the default changes
   * Maia for everyone, so it should take the same two steps any other edit
   * does.
   */
  protected onRestoreButtonClick(section: InstructionSection): void {
    section.restoreDefault();
  }

  // #endregion

  // #region Getters

  /** In the order they open the system prompt. */
  protected get sections(): InstructionSection[] {
    return [this.doctrine, this.rules];
  }

  // #endregion

  // #region Async methods

  private fetchInstructions(): Promise<void> {
    this.isLoading = true;

    return new Rpc()
      .execute('maia:instructions', {})
      .then((response: MaiaInstructionsResponse) => {
        // The doctrine sits at the top level of the response rather than beside
        // the rules, because that is where it has always been and the two
        // repositories deploy apart.
        this.doctrine.fill(response);
        this.rules.fill(response.rules);

        if (response.limits) {
          this.doctrine.setLimits(response.limits.min, response.limits.max);
          this.rules.setLimits(response.limits.rules_min, response.limits.rules_max);
        }
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private publish(section: InstructionSection): Promise<void> {
    section.isSaving = true;
    section.isSaved = false;

    return new Rpc()
      .execute('maia:instructions.update', { [section.field]: section.body })
      .then((response: MaiaInstructionsUpdateResponse) => {
        section.markPublished(section.field === 'rules' ? response.rules : response);
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      })
      .finally(() => {
        section.isSaving = false;
      });
  }

  // #endregion
}

// #region Interfaces

/**
 * One editable text on the page.
 *
 * A class rather than two sets of flat properties on the component: the two
 * boxes do exactly the same things, and the alternative was fourteen fields
 * with a `rules` prefix on half of them and a getter written twice.
 *
 * Every field is assigned in the constructor, which is what keeps it reactive —
 * Vue walks own properties once, at init.
 */
class InstructionSection {
  /** What this text is called when it is sent to maia:instructions.update. */
  public readonly field: string;

  public readonly label: string;

  public readonly hint: string;

  public readonly rows: number;

  public body: string = '';

  /** The text we shipped, for the restore button. */
  public defaultBody: string = '';

  /** What the server last gave us, so we can tell whether anything changed. */
  public savedBody: string = '';

  public isCustomised: boolean = false;

  public editedAt: string = '';

  public editedBy: string = '';

  public minLength: number;

  public maxLength: number;

  public isSaving: boolean = false;

  public isSaved: boolean = false;

  constructor(options: InstructionSectionOptions) {
    this.field = options.field;
    this.label = options.label;
    this.hint = options.hint;
    this.rows = options.rows;
    // Only a starting point. The server sends the bounds it actually validates
    // on, and overwrites these the moment the page loads.
    this.minLength = options.minLength;
    this.maxLength = options.maxLength;
  }

  public fill(payload: MaiaInstructionsSection): void {
    this.body = payload.body;
    this.savedBody = payload.body;
    this.defaultBody = payload.default;
    this.isCustomised = payload.is_customised;
    this.editedAt = payload.edited_at || '';
    this.editedBy = payload.edited_by || '';
  }

  /** The bounds the server actually validates on, which win over the defaults. */
  public setLimits(minLength: number, maxLength: number): void {
    this.minLength = minLength;
    this.maxLength = maxLength;
  }

  public markPublished(result: MaiaInstructionsUpdateResult): void {
    this.savedBody = this.body;
    this.isCustomised = true;
    this.editedAt = result.edited_at || '';
    this.editedBy = result.edited_by || '';
    this.isSaved = true;
  }

  public restoreDefault(): void {
    this.body = this.defaultBody;
    this.isSaved = false;
  }

  public get hasChanges(): boolean {
    return this.body !== this.savedBody;
  }

  public get characterCount(): number {
    return this.body.length;
  }

  public get isTooShort(): boolean {
    return this.characterCount < this.minLength;
  }

  public get isTooLong(): boolean {
    return this.characterCount > this.maxLength;
  }

  public get isOutOfBounds(): boolean {
    return this.isTooShort || this.isTooLong;
  }

  public get canSave(): boolean {
    return this.hasChanges && !this.isSaving && !this.isOutOfBounds;
  }

  public get canRestore(): boolean {
    return this.body !== this.defaultBody;
  }

  public get lengthHint(): string {
    if (this.isTooShort) {
      return `${this.characterCount} of minimum ${this.minLength} characters — too short to publish`;
    }

    if (this.isTooLong) {
      return `${this.characterCount} characters, over the ${this.maxLength} limit`;
    }

    return `${this.characterCount} characters`;
  }

  public get lastEditedLabel(): string {
    if (!this.isCustomised) {
      return 'Never edited — Maia is running on the text Atabix delivered.';
    }

    const who = this.editedBy || 'an account that no longer exists';

    return `Last published by ${who} on ${this.editedAt}.`;
  }
}

interface InstructionSectionOptions {
  field: string;
  label: string;
  hint: string;
  rows: number;
  minLength: number;
  maxLength: number;
}

interface MaiaInstructionsSection {
  body: string;
  default: string;
  is_customised: boolean;
  edited_at: string | null;
  edited_by: string | null;
}

interface MaiaInstructionsResponse extends MaiaInstructionsSection {
  rules: MaiaInstructionsSection;
  limits?: { min: number; max: number; rules_min: number; rules_max: number };
}

interface MaiaInstructionsUpdateResult {
  revision_id: number | null;
  edited_at: string | null;
  edited_by: string | null;
}

interface MaiaInstructionsUpdateResponse extends MaiaInstructionsUpdateResult {
  rules: MaiaInstructionsUpdateResult;
}

// #endregion
